import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ModernStickyHeader } from '../components/ModernStickyHeader';
import { Search, Filter, Grid, List, ExternalLink, Github, Calendar, Tag, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TechStackIcon } from '../components/TechStackIcon';
import { supabase } from '../lib/supabase';
import { Tables } from '../types/supabase';
import { formatCategory } from '../utils/formatCategory';
import { getCustomImageUrl } from '../utils/imageProxy';

// Type definitions
type ProjectRow = Tables<'projects'>;

// Modern Project Card Component
interface ModernProjectCardProps {
  project: ProjectRow;
  index: number;
  layout: 'grid' | 'list';
}

const ModernProjectCard: React.FC<ModernProjectCardProps> = ({ project, index, layout }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (layout === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="group relative bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl overflow-hidden hover:border-orange-300/50 dark:hover:border-orange-600/50 transition-all duration-300"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col md:flex-row">
          {/* Image */}
          <div className="md:w-1/3 aspect-video md:aspect-square">
            <div className="relative w-full h-full overflow-hidden">
              {project.featured_image ? (
                <motion.img
                  src={getCustomImageUrl(project.featured_image, { width: 400, quality: 85, format: 'webp' })}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
                  <span className="text-4xl opacity-30">
                    {project.category === 'Development' ? '💻' : project.category === 'Design' ? '🎨' : '📊'}
                  </span>
                </div>
              )}
              {/* Overlay */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                initial={{ opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 md:p-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-sm font-medium rounded-full">
                  <Tag className="w-3 h-3" />
                  {formatCategory(project.category || 'Uncategorized')}
                </span>
                <h3 className="text-2xl font-light text-gray-900 dark:text-white mt-3 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
                  {project.title}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {project.live_url && (
                  <motion.a
                    href={project.live_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ExternalLink className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </motion.a>
                )}
                {project.github_url && (
                  <motion.a
                    href={project.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Github className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </motion.a>
                )}
              </div>
            </div>

            <div 
              className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: project.description || 'Project description' 
              }}
            />

            {/* Tech Stack */}
            {project.tech_stack && project.tech_stack.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tech_stack.map((tech: string, techIndex: number) => (
                  <span
                    key={techIndex}
                    className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-full flex items-center gap-2"
                  >
                    <TechStackIcon tech={tech} size={16} />
                    {tech}
                  </span>
                ))}
              </div>
            )}

            {/* Status and Date */}
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.status === 'launched' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                project.status === 'in_development' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>
                {project.status === 'launched' ? 'Live MVP' : project.status === 'in_development' ? 'In Development' : 'Concept Phase'}
              </span>
              {project.created_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(project.created_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Grid Layout
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl overflow-hidden hover:border-orange-300/50 dark:hover:border-orange-600/50 transition-all duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <div className="aspect-video relative overflow-hidden">
        {project.featured_image ? (
          <motion.img
            src={getCustomImageUrl(project.featured_image, { width: 600, quality: 85, format: 'webp' })}
            alt={project.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
            <span className="text-4xl opacity-30">
              {project.category === 'Development' ? '💻' : project.category === 'Design' ? '🎨' : '📊'}
            </span>
          </div>
        )}
        
        {/* Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
        
        {/* Action Buttons */}
        <motion.div
          className="absolute top-4 right-4 flex gap-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : -10 }}
          transition={{ duration: 0.3 }}
        >
          {project.live_url && (
            <motion.a
              href={project.live_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ExternalLink className="w-4 h-4 text-white" />
            </motion.a>
          )}
          {project.github_url && (
            <motion.a
              href={project.github_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Github className="w-4 h-4 text-white" />
            </motion.a>
          )}
        </motion.div>

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full">
            <Tag className="w-3 h-3" />
            {project.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-light text-gray-900 dark:text-white mb-3 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
          {project.title}
        </h3>
        
        <div 
          className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4 line-clamp-2 prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ 
            __html: project.description || 'Project description' 
          }}
        />

        {/* Tech Stack */}
        {project.tech_stack && project.tech_stack.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {project.tech_stack.slice(0, 3).map((tech: string, techIndex: number) => (
              <span
                key={techIndex}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full flex items-center gap-1.5"
              >
                <TechStackIcon tech={tech} size={14} />
                {tech}
              </span>
            ))}
            {project.tech_stack.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                +{project.tech_stack.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Status */}
        <div className="flex items-center justify-between">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            project.status === 'launched' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
            project.status === 'in_development' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
            'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}>
            {project.status === 'launched' ? 'Live MVP' : project.status === 'in_development' ? 'In Development' : 'Concept Phase'}
          </span>
          {project.created_at && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(project.created_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export function ProjectsPage() {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch projects from Supabase
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching projects:', error);
          setProjects([]);
        } else {
          setProjects(data || []);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(projects.map(p => p.category).filter(Boolean))];
    return cats;
  }, [projects]);

  // Filter projects (simplified for small portfolio)
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        project.tech_stack?.some((tech: string) => tech.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(project => project.category === selectedCategory);
    }

    // Sort by most recent
    filtered = [...filtered].sort((a, b) => 
      new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );

    return filtered;
  }, [projects, searchTerm, selectedCategory]);

  // Navigation links for projects page
  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Projects', href: '/projects' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/#contact' },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
        <ModernStickyHeader navLinks={navLinks} />
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
      {/* Fixed Header with proper z-index */}
      <ModernStickyHeader navLinks={navLinks} />
      
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-200/20 dark:bg-orange-800/20 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-40 right-20 w-24 h-24 bg-teal-200/20 dark:bg-teal-800/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-blue-200/20 dark:bg-blue-800/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 pt-18">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pt-16 pb-12 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-7xl mx-auto">
            {/* Back to Home Link */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to="/"
                className="group inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors duration-300"
              >
                <motion.div
                  whileHover={{ x: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowLeft className="w-4 h-4" />
                </motion.div>
                <span className="text-sm font-medium">Back to Home</span>
              </Link>
            </motion.div>

            <div className="text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-5xl font-light text-gray-900 dark:text-white mb-4"
              >
                Selected Projects
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto"
              >
                A curated collection of projects showcasing modern web development and design
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400"
              >
                <span>{filteredProjects.length} featured projects</span>
                <span>•</span>
                <span>Handpicked for quality</span>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Simplified Controls */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="px-4 sm:px-6 lg:px-8 mb-12"
        >
          <div className="max-w-7xl mx-auto">
            <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white/50 dark:bg-gray-700/50 border border-gray-200/50 dark:border-gray-600/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200"
                  />
                </div>

                {/* Category Tabs */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category || 'All')}
                        className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                          selectedCategory === category
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {formatCategory(category || 'Uncategorized')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Layout Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setLayout('grid')}
                    className={`p-2 rounded-md transition-colors duration-200 ${
                      layout === 'grid' 
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setLayout('list')}
                    className={`p-2 rounded-md transition-colors duration-200 ${
                      layout === 'list' 
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Projects Showcase */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="px-4 sm:px-6 lg:px-8 pb-24"
        >
          <div className="max-w-7xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${layout}-${selectedCategory}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={
                  layout === 'grid'
                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                    : 'space-y-6'
                }
              >
                {filteredProjects.map((project, index) => (
                  <Link key={project.id} to={`/project/${project.slug || project.id}`}>
                    <ModernProjectCard
                      project={project}
                      index={index}
                      layout={layout}
                    />
                  </Link>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Enhanced Empty State */}
            {filteredProjects.length === 0 && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <div className="text-8xl mb-6">🚀</div>
                <h3 className="text-2xl font-light text-gray-900 dark:text-white mb-4">
                  Crafting Amazing Projects
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
                  I'm currently working on some exciting new projects. Check back soon or get in touch to discuss custom work.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/#contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-200"
                  >
                    <span>Get In Touch</span>
                  </Link>
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/50 dark:bg-gray-800/50 hover:bg-white/70 dark:hover:bg-gray-800/70 text-gray-900 dark:text-white border border-gray-200/50 dark:border-gray-700/50 rounded-lg transition-colors duration-200"
                  >
                    <span>Read My Blog</span>
                  </Link>
                </div>
      </motion.div>
            )}
          </div>
        </motion.section>
      </div>

    </div>
  );
}